/**
 * useReveal — tiny IntersectionObserver hook for scroll-triggered reveals.
 *
 * Returns [ref, revealed]. Once revealed, the observer disconnects so we
 * never re-animate. Honours `prefers-reduced-motion` by returning true
 * immediately so content is shown without animation for accessibility.
 *
 * Usage:
 *   const [ref, on] = useReveal();
 *   <div ref={ref} className={`reveal ${on ? "reveal-in" : ""}`}>...</div>
 */
import { useEffect, useRef, useState } from "react";

export function useReveal({ threshold = 0.12, rootMargin = "0px 0px -10% 0px" } = {}) {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    // Respect reduced-motion preference — show content instantly.
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setRevealed(true);
      return;
    }

    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setRevealed(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setRevealed(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold, rootMargin },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [threshold, rootMargin]);

  return [ref, revealed];
}

export default useReveal;
